// Resolves a dimension value to its leading icon.
//
// Official brand assets are used for ad platforms (Google, Meta, Microsoft);
// every other dimension gets a neutral Untitled-UI glyph — one per dimension,
// with a few per-value overrides (device / IP usage / status) where a specific
// glyph reads better. Brand marks are NEVER hand-rolled.

// Brand marks are imported as URLs and rendered as image elements, NOT as inline
// SVGR `ReactComponent`s. Two reasons: (1) the Vite demos build (vite-plugin-svgr
// v5) no longer provides the CRA `ReactComponent` named export, only the `?react`
// query — which in turn breaks CRA's own dev build; a plain URL import resolves
// in both. (2) Inline brand SVGs share ids (pattern0/clip0) and `url(#id)`
// resolves to the first match in DOM order, so one logo renders another's fill.
// An image element isolates each in its own document. These are full-color marks
// that never need recoloring, so rendering them as images loses nothing.
import { CircleFlag } from 'react-circle-flags'
import googleLogo from 'assets/img/google.svg'
import metaLogo from 'assets/img/meta.svg'
import microsoftLogo from 'assets/img/microsoft.svg'
import {
	AlertCircle,
	AlertTriangle,
	Announcement01,
	Briefcase01,
	Building01,
	Building07,
	CheckCircle,
	Globe01,
	Hash02,
	Home02,
	LayoutAlt01,
	LayoutGrid01,
	Lightning01,
	Link01,
	MarkerPin01,
	Monitor04,
	Phone01,
	SearchLg,
	Server01,
	Server04,
	SlashCircle01,
	Tablet01,
	Tag01
} from './icons'
import { DimensionId } from '../types'
// OS / browser marks reuse the exact react-icons set the Click Forensics page uses
// (utils/click.tsx DeviceOsMapper / DeviceBrowserMapper), with a proper Android
// icon (forensics shows the Linux penguin) and Firefox added.
import { RiAppleLine, RiWindowsLine, RiAndroidLine, RiChromeLine, RiSafariLine, RiFirefoxLine, RiEdgeFill } from 'react-icons/ri'
import { SiLinux } from 'react-icons/si'

type Glyph = React.ComponentType<{ className?: string }>

const BRAND_SRC: Record<string, string> = {
	google: googleLogo,
	meta: metaLogo,
	microsoft: microsoftLogo
}

// Raw ad-platform code (clk.ad_platform, the `ad_platform` dimension's value) ->
// brand key. Lets live ad_platform rows fly the right logo without the demo
// registry.
const PLATFORM_CODE_TO_BRAND: Record<string, string> = {
	GADS: 'google',
	FADS: 'meta',
	BADS: 'microsoft'
}

/** Default glyph per dimension. */
const DIMENSION_GLYPH: Record<DimensionId, Glyph> = {
	country: Globe01,
	region: MarkerPin01,
	city: Building07,
	ad_platform: Globe01,
	ad_account: Briefcase01,
	campaign_type: Lightning01,
	campaign: Announcement01,
	ad_group: LayoutGrid01,
	keyword: SearchLg,
	isp: Server01,
	ip_usage: Server04,
	device_type: Monitor04,
	os: Monitor04,
	browser: Globe01,
	placement: LayoutAlt01,
	target_url: Link01,
	referrer: Globe01,
	status: AlertCircle,
	utm_source: Tag01,
	utm_campaign: Tag01,
	utm_content: Tag01,
	utm_medium: Tag01,
	utm_term: Hash02
}

/** Per-value overrides where a specific glyph is clearer than the dimension
 *  default. Keys are lower-cased raw values; the live engine's own spellings
 *  (`consumer` / `corporate` / `hosting`, `computer` / `phone`) sit beside the
 *  prototype ids so both resolve. */
const PER_VALUE: Record<DimensionId, Record<string, Glyph>> = {
	device_type: {
		desktop: Monitor04,
		computer: Monitor04,
		tablet: Tablet01,
		mobile: Phone01,
		phone: Phone01,
		smartphone: Phone01
	},
	ip_usage: {
		residential: Home02,
		consumer: Home02,
		business: Building01,
		corporate: Building01,
		datacenter: Server01,
		'data center': Server01,
		hosting: Server01,
		reserved: Server04
	},
	status: { bad: SlashCircle01, suspicious: AlertTriangle, good: CheckCircle, valid: CheckCircle },
	os: {
		macos: RiAppleLine,
		'mac os': RiAppleLine,
		'mac os x': RiAppleLine,
		'os x': RiAppleLine,
		ios: RiAppleLine,
		windows: RiWindowsLine,
		linux: SiLinux,
		ubuntu: SiLinux,
		android: RiAndroidLine
	},
	browser: {
		chrome: RiChromeLine,
		'chrome mobile': RiChromeLine,
		safari: RiSafariLine,
		'mobile safari': RiSafariLine,
		firefox: RiFirefoxLine,
		edge: RiEdgeFill,
		'microsoft edge': RiEdgeFill
	}
}

const perValueGlyph = (dimensionId: DimensionId | undefined, valueId: string | undefined): Glyph | undefined => {
	if (!dimensionId || !valueId) return undefined
	const table = PER_VALUE[dimensionId]
	if (!table) return undefined
	const raw = valueId.toLowerCase().trim()
	return table[raw] ?? table[raw.replace(/[_-]+/g, ' ')]
}

const isTwoLetter = (s: string | null | undefined): s is string => !!s && /^[a-z]{2}$/i.test(s)

/** The ISO country code a value flies, if any: the country dimension's own
 *  value, or the parent country a region / city row carries as its icon hint. */
export const countryCodeFor = (dimensionId: DimensionId, valueId: string, iconMeta?: string | null): string | null =>
	(dimensionId === 'country' && isTwoLetter(valueId) ? valueId : null) ??
	((dimensionId === 'region' || dimensionId === 'city') && isTwoLetter(iconMeta) ? iconMeta : null)

/** The brand key (google / meta / microsoft) a value flies, if any: the ad
 *  platform's own value, or the platform the engine attached to a channel row. */
export const brandKeyFor = (dimensionId: DimensionId, valueId: string, iconMeta?: string | null): string | undefined =>
	(dimensionId === 'ad_platform' ? PLATFORM_CODE_TO_BRAND[valueId?.toUpperCase?.() ?? valueId] : undefined) ??
	(iconMeta ? PLATFORM_CODE_TO_BRAND[iconMeta.toUpperCase()] : undefined)

const FlagMark = ({ iso, className }: { iso: string; className: string }) => (
	<span className={`inline-flex shrink-0 items-center justify-center ${className}`}>
		<CircleFlag countryCode={iso.toLowerCase()} className="h-full w-full rounded-full" />
	</span>
)

const BrandMark = ({ brandKey, className }: { brandKey: string; className: string }) => (
	<span className={`inline-flex shrink-0 items-center justify-center ${className}`}>
		<img src={BRAND_SRC[brandKey]} alt={`${brandKey} logo`} className="w-full h-full object-contain" />
	</span>
)

/** Dimensions whose COLOUR carries the meaning. A chart draws no mark for these:
 *  green, amber and red already say valid, suspicious and bad, so a glyph beside
 *  the swatch only repeats it. The table still draws them. */
const COLOUR_IS_THE_MEANING = new Set<DimensionId>(['status'])

/**
 * The leading mark for a CHART category (DEV-2432 §2.4): the country flag for a
 * geography (regions and cities fly their parent country's flag), then the ad
 * platform's brand mark for a channel value, then the value's OWN glyph from the
 * per-value table (IP usage, device type, OS, browser). A chart deliberately
 * stops there and never falls back to the dimension's default glyph: ISP,
 * Placement, Target URL and the UTM dimensions have no per-value table, so every
 * one of their values would fly the same mark, which tells the reader nothing.
 * Renders nothing when there is no mark, so the caller's gap collapses.
 */
export const CatMark = ({
	dimensionId,
	valueId,
	iconMeta,
	className = 'h-4 w-4'
}: {
	dimensionId?: DimensionId
	valueId?: string
	iconMeta?: string | null
	className?: string
}) => {
	if (!dimensionId || valueId == null) return null
	const iso = countryCodeFor(dimensionId, valueId, iconMeta)
	if (iso) return <FlagMark iso={iso} className={className} />
	const brandKey = brandKeyFor(dimensionId, valueId, iconMeta)
	if (brandKey && BRAND_SRC[brandKey]) return <BrandMark brandKey={brandKey} className={className} />
	const Glyph = COLOUR_IS_THE_MEANING.has(dimensionId) ? undefined : perValueGlyph(dimensionId, valueId)
	if (!Glyph) return null
	return <Glyph className={`shrink-0 text-gray-500 ${className}`} />
}

export interface SourceIconProps {
	dimensionId: DimensionId
	valueId: string
	/**
	 * Per-row icon hint from the live engine: the ad-platform code (channel dims,
	 * e.g. "GADS") or the country ISO (region/city). Lets those rows resolve a
	 * logo/flag their value alone can't. Absent on mock rows (registry handles it).
	 */
	iconMeta?: string | null
	className?: string
}

export const SourceIcon = ({ dimensionId, valueId, iconMeta, className = 'w-5 h-5' }: SourceIconProps) => {
	// Geography → circular country flag (react-circle-flags renders a CDN-backed
	// <img>, so it's crisp everywhere incl. Windows). The country dimension's value
	// IS the ISO-3166 alpha-2 code (geo_country, e.g. "US"), so fly it directly;
	// Region/City take their parent country's code from the row's `iconMeta`.
	// Guard on a real 2-letter code so aggregates like "Other" keep their glyph
	// instead of a broken flag image.
	const isoCode = countryCodeFor(dimensionId, valueId, iconMeta)
	if (isoCode) return <FlagMark iso={isoCode} className={className} />

	// Channel / ad dimensions (ad platform, ad account, campaign type, campaign,
	// ad group, keyword) → the ad platform's brand logo. The ad_platform dimension
	// carries the platform in its own value; the others need it supplied per row
	// (not yet wired for live data — they fall back to the dimension glyph).
	// Other channel dims (ad account, campaign, keyword, …) carry their ad
	// platform's code as the row's iconMeta.
	const brandKey = brandKeyFor(dimensionId, valueId, iconMeta)
	if (brandKey && BRAND_SRC[brandKey]) return <BrandMark brandKey={brandKey} className={className} />

	const Glyph = perValueGlyph(dimensionId, valueId) ?? DIMENSION_GLYPH[dimensionId] ?? Globe01
	return <Glyph className={`shrink-0 text-gray-500 ${className}`} />
}
